import { useAtomValue } from 'jotai';

import type { User } from '@/types/directory/User';

import { companyAtom } from '@/atoms/company';
import { reviewAnswerAtom } from '@/atoms/reviewAnswer';
import { userInstanceAtom } from '@/atoms/userDetails';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';
import { replaceDynamicTags } from '@/utils/dynamicTags';
import { getLocalizedText, type LocalizedText } from '@/utils/localization';

type EmployeeInput = Pick<User, 'firstName'> | null | undefined;

// The localised names of the competency / company value a generated fallback question is about.
// Passed as the full dictionary so `getLocalizedText` applies its own language fallback to the name,
// independently of the sentence around it (#15386).
interface OwnerNamesInput {
    competencyName?: LocalizedText | null;
    companyValueName?: LocalizedText | null;
}

export interface ReplaceDynamicTagsArgs extends OwnerNamesInput {
    text: string | undefined | null;
    employee?: EmployeeInput;
}

export const useReplaceDynamicTags = () => {
    const company = useAtomValue(companyAtom);
    const reviewAnswer = useAtomValue(reviewAnswerAtom);
    const user = useAtomValue(userInstanceAtom);
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();

    return ({
        text,
        employee,
        competencyName,
        companyValueName,
    }: ReplaceDynamicTagsArgs): string => {
        const companyNames = company?.company.names ?? reviewAnswer?.companyNames;
        const companyName =
            getLocalizedText(companyNames) ||
            (companyNames ? Object.values(companyNames).find(Boolean) : undefined) ||
            user?.companyName ||
            '';
        const managerLabel = getManagerLabel();
        const employeeFirstName =
            employee?.firstName?.trim() || t('dynamicTags.placeholderEmployeeFirstName');

        return replaceDynamicTags({
            text,
            companyName,
            managerLabel,
            employeeFirstName,
            competencyName: competencyName ? getLocalizedText(competencyName) : undefined,
            companyValueName: companyValueName ? getLocalizedText(companyValueName) : undefined,
        });
    };
};
